import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SocialShare } from '@/components/SocialShare';

const SilenceAbsenceNoise = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <Badge className="mb-4">Mindfulness</Badge>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              Silence – The Absence of Noise That Can Change Life
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>August 16, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>4 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Piotr</span>
              </div>
            </div>
            
            <SocialShare 
              url={window.location.href}
              title="Silence – The Absence of Noise That Can Change Life"
              description="We live in a world filled with noise. True silence has become rare, almost uncomfortable. Yet silence is not empty; it is profoundly restorative."
            />
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src="/lovable-uploads/1c01990f-9d38-49ae-9b00-58eea417b3b7.png" 
              alt="Person sitting alone on bench by water in contemplative silence representing the power of quiet moments"
              className="w-full h-[400px] md:h-[500px] object-cover grayscale"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="text-xl leading-relaxed text-muted-foreground mb-8 font-light italic">
              We live in a world filled with noise – traffic, notifications, endless chatter. True silence has become rare, almost uncomfortable. Yet silence is not empty; it is profoundly restorative.
            </div>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The science of silence</h2>

              <p>
                Research shows that silence reduces stress and promotes brain growth. A study on mice demonstrated that <strong className="text-accent">two hours of silence daily led to the development of new cells in the hippocampus</strong> – the brain region linked to memory and learning (Kirste et al., 2013).
              </p>

              <p>
                For humans, even short periods of silence lower blood pressure, improve focus, and enhance creativity (Halpern, 2014). <strong className="text-accent">Silence allows the nervous system to reset, offering relief from constant stimulation</strong>.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">How to practice silence</h2>

              <ul className="space-y-3 ml-6">
                <li><strong className="text-accent">Begin the day with five minutes of quiet</strong> before looking at devices.</li>
                <li><strong className="text-accent">Create silent rituals</strong> – like meditative walks or sitting in stillness.</li>
                <li><strong className="text-accent">Reduce background noise</strong> such as TV or music during meals.</li>
              </ul>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Conclusion</h2>

              <p>
                Silence is not the absence of life but its depth. By cultivating silence, we rediscover clarity, balance, and inner peace.
              </p>

              <p className="text-xl font-light italic text-accent">
                In silence, we hear the whispers of wisdom within.
              </p>

              <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References</h3>
                <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                  <li>Kirste, I., Nicola, Z., Kronenberg, G., Walker, T. L., Liu, R. C., & Kempermann, G. (2013). Is silence golden? Effects of auditory stimuli and their absence on adult hippocampal neurogenesis. <em>Brain Structure and Function, 219</em>(2), 1–8.</li>
                  <li>Halpern, D. (2014). <em>The Power of Silence in a Noisy World</em>. Penguin.</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default SilenceAbsenceNoise;
